// Single-source print stylesheet, injected into <head> (not as inline <style>).
// The screen view mirrors the print view at 1:1 so "what you see is what prints".
//
// Why an in-head <link>/<style> vs inline JSX <style>:
//   • @page is a document-level at-rule. If a stale prior @page rule exists in
//     index.css or any other sheet, the LAST one in document order wins. By
//     injecting our @page rule into <head> on mount we guarantee it loads
//     before the React-rendered tree styles and stays valid.
//   • This also keeps the cascade tidy: print rules are scoped under a real
//     stylesheet, not a node deeply nested in the body.

import { useEffect, useLayoutEffect } from "react";

// Width of an A4 portrait page minus 10 mm margin per side = 190 mm.
// The container is sized exactly that width so the browser never has to
// shrink-to-fit (which was producing the squashed strip).
export const PRINT_PAGE_WIDTH_MM = 190;
export const PRINT_PAGE_HEIGHT_MM = 277; // 297 mm − 20 mm = 277 mm

export const printStylesheetId = "ccs-print-stylesheet";

export const printCss = `
/* =========================================================
   Camera Club Score — print stylesheet (head-injected)
   ========================================================= */

/* Reset for parents while a print page is mounted */
html, body, #root {
  background: #ffffff !important;
  color: #111111 !important;
  margin: 0 !important;
  padding: 0 !important;
  min-height: 0 !important;
  height: auto !important;
  overflow: visible !important;
  width: auto !important;
  max-width: none !important;
  transform: none !important;
}

/* Print container — sized to A4 landscape so the browser does NOT
   shrink-to-fit when printing. */
.cc-print {
  background: #ffffff;
  color: #111111;
  font-family: 'IBM Plex Sans', system-ui, Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.35;
  width: ${PRINT_PAGE_WIDTH_MM}mm;
  max-width: ${PRINT_PAGE_WIDTH_MM}mm;
  margin: 0 auto;
  padding: 6mm 0;
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.cc-print * { box-sizing: border-box; }

/* Toolbar (screen only) */
.cc-print-toolbar {
  display: flex; gap: 10px; justify-content: flex-end;
  margin-bottom: 6mm;
}
.cc-print-btn {
  font-family: inherit;
  font-size: 11pt;
  padding: 2mm 5mm;
  border: 1px solid #999;
  background: #ffffff;
  color: #111111;
  cursor: pointer;
}
.cc-print-btn:hover { background: #f4f4f4; }
.cc-print-btn.primary {
  background: #FBBF24;
  border-color: #d4a017;
  color: #111;
  font-weight: 600;
}
.cc-print-btn.primary:hover { background: #f59e0b; }

/* Header */
.cc-print-header {
  text-align: center;
  border-bottom: 2px solid #111;
  padding-bottom: 3mm;
  margin-bottom: 4mm;
}
.cc-print-header .club-name {
  font-size: 16pt;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #111;
  margin-bottom: 1.5mm;
}
.cc-print-header .comp-title {
  font-size: 13pt;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #111;
  margin-bottom: 1mm;
}
.cc-print-header .meta-row {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9pt;
  color: #555;
  display: flex;
  justify-content: center;
  gap: 5mm;
  flex-wrap: wrap;
}
.cc-print-header .meta-row strong { color: #111; font-weight: 600; }
.cc-print-header .page-sub {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 8pt;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #777;
  margin-top: 2mm;
}

/* Summary strip (results only) */
.cc-print-summary {
  display: flex; gap: 8mm; justify-content: center;
  margin: 2mm 0 3mm;
  font-size: 10pt; color: #333;
}
.cc-print-summary strong { color: #111; }

/* Table */
.cc-print-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 10.5pt;
}
.cc-print-table th, .cc-print-table td {
  border: 1px solid #999;
  padding: 2mm 2.5mm;
  text-align: left;
  vertical-align: middle;
  word-break: break-word;
}
.cc-print-table thead th {
  background: #f3f3f3;
  font-size: 8.5pt;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #222;
  white-space: nowrap;
}
.cc-print-table td.num { text-align: center; font-family: 'IBM Plex Mono', monospace; color: #666; white-space: nowrap; }
.cc-print-table td.mono { font-family: 'IBM Plex Mono', monospace; font-weight: 600; white-space: nowrap; }
.cc-print-table td.score { font-family: 'IBM Plex Mono', monospace; font-weight: 700; text-align: right; padding-right: 4mm; }
.cc-print-table .box-cell { text-align: center; padding: 1.5mm; }
.cc-print-table .sq-box {
  display: inline-block;
  width: 7mm;
  height: 7mm;
  border: 1.5px solid #333;
  background: #ffffff;
  vertical-align: middle;
}

.cc-print-table .pcell { font-weight: 600; }
.cc-print-table .pcell-FIRST            { color: #92400e; background: #fef3c7; }
.cc-print-table .pcell-SECOND           { color: #374151; background: #f3f4f6; }
.cc-print-table .pcell-THIRD            { color: #92400e; background: #ffedd5; }
.cc-print-table .pcell-HIGHLY_COMMENDED { color: #155e75; background: #ecfeff; }
.cc-print-table .pcell-COMMENDED        { color: #6d28d9; background: #f5f3ff; }

/* Multi-page support */
.cc-print-table thead { display: table-header-group; }
.cc-print-table tfoot { display: table-footer-group; }
.cc-print-table tr {
  page-break-inside: avoid;
  break-inside: avoid;
}

.cc-print-legend {
  display: flex; gap: 8mm; margin-top: 3mm;
  font-size: 9pt; color: #555; flex-wrap: wrap;
  border-top: 1px solid #ddd; padding-top: 2mm;
}

.cc-print-footer {
  margin-top: 5mm; padding-top: 2mm;
  border-top: 1px solid #ddd;
  font-size: 8pt; color: #888;
  text-align: center;
  font-family: 'IBM Plex Mono', monospace;
}

/* =========================================================
   @page + @media print rules
   ========================================================= */
@page {
  size: A4 portrait;
  margin: 10mm;
}

@media print {
  /* Hide every screen-only chrome */
  #emergent-badge,
  [data-emergent-badge],
  .no-print,
  .cc-print-toolbar { display: none !important; }

  /* Force a clean document — break out of any flex/grid parents */
  html, body, #root {
    width: ${PRINT_PAGE_WIDTH_MM}mm !important;
    max-width: ${PRINT_PAGE_WIDTH_MM}mm !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    color: #111111 !important;
    min-height: 0 !important;
    height: auto !important;
    overflow: visible !important;
    display: block !important;
    transform: none !important;
    zoom: 1 !important;
  }

  .cc-print {
    width: ${PRINT_PAGE_WIDTH_MM}mm !important;
    max-width: ${PRINT_PAGE_WIDTH_MM}mm !important;
    margin: 0 !important;
    padding: 0 !important;
    transform: none !important;
    zoom: 1 !important;
  }

  .cc-print, .cc-print * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
}
`;

// Inject the print stylesheet into <head>. Removed on unmount.
export function usePrintStylesheet() {
  useLayoutEffect(() => {
    let el = document.getElementById(printStylesheetId);
    let created = false;
    if (!el) {
      el = document.createElement("style");
      el.id = printStylesheetId;
      el.setAttribute("data-purpose", "ccs-print");
      document.head.appendChild(el);
      created = true;
    }
    el.textContent = printCss;
    // Mark the host doc so we can also style based on a class if needed.
    document.documentElement.classList.add("cc-print-mode");
    return () => {
      document.documentElement.classList.remove("cc-print-mode");
      if (created && el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  // Also hide the platform badge during this page's lifetime.
  useEffect(() => {
    const badge = document.getElementById("emergent-badge");
    const prev = badge ? badge.style.display : null;
    if (badge) badge.style.display = "none";
    return () => { if (badge) badge.style.display = prev || ""; };
  }, []);
}
